import React, { useState } from 'react'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon
} from '@mui/icons-material'
import { useAuth } from '../hooks/useAuth'
import { auth, db } from '../firebase/config'
import { collection, query, where, getDocs, addDoc } from 'firebase/firestore'
import toast from 'react-hot-toast'

const CreateClass = ({ userData, onBack }) => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    className: '',
    description: '',
    autoJoinCode: true,
    joinCode: ''
  })
  const [errors, setErrors] = useState({})

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }))
    }
  }

  const handleSwitchChange = (event) => {
    const autoJoinCode = event.target.checked
    setFormData(prev => ({
      ...prev,
      autoJoinCode,
      joinCode: autoJoinCode ? '' : prev.joinCode
    }))
  }

  const generateJoinCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
    let result = ''
    for (let i = 0; i < 6; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length))
    }
    return result
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.className.trim()) {
      newErrors.className = 'Sınıf adı gerekli'
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Sınıf açıklaması gerekli'
    }

    if (!formData.autoJoinCode) {
      if (!formData.joinCode.trim()) {
        newErrors.joinCode = 'Join code gerekli'
      } else if (formData.joinCode.length !== 6) {
        newErrors.joinCode = 'Join code 6 karakter olmalı'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const checkJoinCodeExists = async (joinCode) => {
    try {
      const classesRef = collection(db, 'classes')
      const q = query(classesRef, where('joinCode', '==', joinCode))
      const querySnapshot = await getDocs(q)
      return !querySnapshot.empty
    } catch (error) {
      console.error('Error checking join code:', error)
      throw error
    }
  }

  const handleCreateClass = async () => {
    if (!validateForm()) {
      return
    }

    setLoading(true)

    try {
      const teacherId = user.uid
      const teacherName = userData?.username || user?.displayName || user?.email

      // Join code belirle
      let finalJoinCode
      if (formData.autoJoinCode) {
        finalJoinCode = generateJoinCode()
      } else {
        // Manuel kod için kontrol et
        const exists = await checkJoinCodeExists(formData.joinCode.toUpperCase())
        if (exists) {
          setErrors({ joinCode: 'Bu join code zaten kullanılıyor' })
          toast.error('Bu kod başka bir sınıf için kullanılmaktadır!')
          setLoading(false)
          return
        }
        finalJoinCode = formData.joinCode.toUpperCase()
      }

      // Sınıf objesi oluştur
      const newClass = {
        className: formData.className.trim(),
        teacherId,
        teacherName,
        description: formData.description.trim(),
        level: '', // Seviye olmadan
        joinCode: finalJoinCode,
        createdAt: new Date(),
        studentIds: []
      }

      // Firestore'a kaydet
      await addDoc(collection(db, 'classes'), newClass)

      toast.success('Sınıf başarıyla oluşturuldu!')
      onBack() // Sınıflar sayfasına dön
    } catch (error) {
      console.error('Error creating class:', error)
      toast.error('Sınıf oluşturulurken hata oluştu: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* App Bar */}
      <AppBar position="static" sx={{ 
        background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onBack}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Yeni Sınıf Oluştur
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ py: 3 }}>
        {/* Form */}
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            {/* Sınıf Adı */}
            <Typography variant="h6" color="#6E6D6D" gutterBottom sx={{ fontWeight: 'bold' }}>
              Sınıf Adı
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Örn: 9-A Sınıfı"
              value={formData.className}
              onChange={(e) => handleInputChange('className', e.target.value)}
              error={!!errors.className}
              helperText={errors.className}
              sx={{ mb: 3 }}
            />

            {/* Sınıf Açıklaması */}
            <Typography variant="h6" color="#6E6D6D" gutterBottom sx={{ fontWeight: 'bold' }}>
              Sınıf Açıklaması
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Sınıfınız hakkında kısa bir açıklama yazın..."
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              error={!!errors.description}
              helperText={errors.description}
              multiline
              rows={3}
              sx={{ mb: 3 }}
            />

            {/* Sınıf Ayarları */}
            <Typography variant="h6" color="#6E6D6D" gutterBottom sx={{ fontWeight: 'bold' }}>
              Sınıf Ayarları
            </Typography>

            {/* Otomatik Join Code */}
            <Card sx={{ mb: 2, backgroundColor: '#f8f9fa' }}>
              <CardContent sx={{ py: 2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.autoJoinCode}
                      onChange={handleSwitchChange}
                      color="primary"
                    />
                  }
                  label="Otomatik Join Code Oluştur"
                  sx={{ width: '100%' }}
                />
              </CardContent>
            </Card>

            {/* Manuel Join Code */}
            {!formData.autoJoinCode && (
              <>
                <Typography variant="body2" color="#6E6D6D" gutterBottom>
                  Manuel Join Code
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Özel bir join code girin (6 karakter, benzersiz olmalı)
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Özel join code girin (6 karakter)"
                  value={formData.joinCode}
                  onChange={(e) => handleInputChange('joinCode', e.target.value.toUpperCase())}
                  error={!!errors.joinCode}
                  helperText={errors.joinCode}
                  inputProps={{ maxLength: 6 }}
                  sx={{ mb: 3 }}
                />
              </>
            )}
          </CardContent>
        </Card>

        {/* Create Button */}
        <Button
          variant="contained"
          fullWidth
          size="large"
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
          onClick={handleCreateClass}
          disabled={loading}
          sx={{
            py: 1.5,
            fontSize: '1.1rem',
            fontWeight: 'bold',
            background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)'
          }}
        >
          {loading ? 'Oluşturuluyor...' : 'Sınıfı Oluştur'}
        </Button>
      </Container>
    </Box>
  )
}

export default CreateClass
